import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ZefFuseModule } from '@zerops/zef/fuse';
import { ZefFuseService } from '@zerops/zef/fuse';
import { getMaxAssignableProjectRole } from '@zerops/zef/permission';
import { LocalSearchFieldModule } from '@zerops/zui/local-search-field';
import { ZuiTagModule } from '@zerops/zui/tag';
import { ClientUser, ClientUserRole } from '@zerops/models/client-user';
import { AssignableProjectRole, Project } from '@zerops/models/project';

interface ProjectAccessSelection {
  projectId: string;
  roleCode: AssignableProjectRole;
  project: Project;
}

// Full access is expressed as ADMIN — the popover never offers OWNER.
type RoleSelection = AssignableProjectRole | 'NONE';

interface ProjectItemData {
  project: Project;
  projectId: string;
  displayName: string;
  tags: string[];
  selectedRole: RoleSelection;
  maxAssignableRole: AssignableProjectRole | null;
  canAssignAdmin: boolean;
  canAssignReadOnly: boolean;
}

interface ProjectSearchResult {
  item: ProjectItemData;
  matches?: any;
}

@Component({
  selector: 'zui-add-project-access-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatRadioModule,
    MatCardModule,
    MatTooltipModule,
    ZefFuseModule,
    LocalSearchFieldModule,
    ZuiTagModule
  ],
  templateUrl: './add-project-access-popover.component.html',
  styleUrl: './add-project-access-popover.component.scss'
})
export class AddProjectAccessPopoverComponent {
  // # Deps
  #fuse = inject(ZefFuseService<ProjectItemData>);

  // # Inputs
  projects = input.required<Project[]>();
  currentUser = input.required<ClientUser>();
  editingUserRole = input<ClientUserRole>();

  // # Outputs
  projectsConfirmed = output<ProjectAccessSelection[]>();
  cancelled = output<void>();

  // # Internal State
  searchQuery = signal<string>('');
  projectSelections = signal<Map<string, RoleSelection>>(new Map());

  // # Constants
  clientUserRole = ClientUserRole;
  readonly SEARCH_KEYS = [
    { name: 'displayName', weight: 0.7 },
    { name: 'tags', weight: 0.3 }
  ];
  readonly FEATURE_ID = 'project-access-override-search';

  // # Computed
  projectItems = computed<ProjectItemData[]>(() => {
    const currentUser = this.currentUser();
    const selections = this.projectSelections();

    return this.projects().map(project => {
      const maxRole = this.#getMaxAssignableRole(project, currentUser);

      return {
        project,
        projectId: project.id,
        displayName: project.name,
        tags: project.tagList || [],
        selectedRole: selections.get(project.id) || 'NONE',
        maxAssignableRole: maxRole,
        canAssignAdmin: maxRole === ClientUserRole.Admin,
        canAssignReadOnly: maxRole === ClientUserRole.Admin || maxRole === ClientUserRole.ReadOnly
      };
    });
  });

  filteredProjects = computed(() => {
    const query = this.searchQuery().trim();
    const items = this.projectItems();

    // Refresh the fuse instance when items change
    this.#fuse.refresh(items, this.SEARCH_KEYS, this.FEATURE_ID);

    // Perform fuzzy search
    return this.#fuse.search(items, query, this.SEARCH_KEYS, this.FEATURE_ID) as ProjectSearchResult[];
  });

  selectedCount = computed(() => {
    let count = 0;
    this.projectSelections().forEach(role => {
      if (role !== 'NONE') count++;
    });
    return count;
  });

  // # Methods
  onSearchChange(query: string) {
    this.searchQuery.set(query);
  }

  #getMaxAssignableRole(project: Project, currentUser: ClientUser): AssignableProjectRole | null {
    return getMaxAssignableProjectRole(project, currentUser);
  }

  onRoleToggle(projectId: string, role: AssignableProjectRole | 'UNSET') {
    const current = new Map(this.projectSelections());
    const currentRole = current.get(projectId);

    if (currentRole === role || role === 'UNSET') {
      current.delete(projectId); // Uncheck
    } else {
      current.set(projectId, role); // Check
    }

    this.projectSelections.set(current);
  }

  onConfirm() {
    const result: ProjectAccessSelection[] = [];
    const items = this.projectItems();

    this.projectSelections().forEach((role, projectId) => {
      if (role !== 'NONE') {
        const item = items.find(i => i.projectId === projectId);
        if (item) {
          result.push({
            projectId,
            roleCode: role,
            project: item.project
          });
        }
      }
    });

    this.projectsConfirmed.emit(result);
    this.#reset();
  }

  onCancel() {
    this.#reset();
    this.cancelled.emit();
  }

  reset() {
    this.#reset();
  }

  #reset() {
    this.projectSelections.set(new Map());
    this.searchQuery.set('');
  }
}
